import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { PrimaryButton, SecondaryButton } from '@cb/apricot-react';
import { Grid, LinearProgress } from '@mui/material';

import { FormGroup } from 'src/components';
import { SchoolHeader } from 'src/components/ai-form';
import { APP_URLS, COLORS } from 'src/constants';
import { AIFormProvider, useAuth } from 'src/contexts';
import { useToasts } from 'src/hooks';
import {
  checkSchoolFormValidation,
  delay,
  generatePDF,
  getAIFormRequestData,
  getUpdatedAIFormValues,
  uploadSchoolFile,
} from 'src/utils';
import { TAIForm, FormTypes } from 'src/types';

import { defaultValues, formSubmitGroup, schoolForms } from './constants';
import { testingPlanForms } from '../testing-plan/constants';
import { saveAIForm, submitAIForm } from 'src/apis';

const School: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRefs = useRef<Nullable<HTMLDivElement>[]>([]);
  let refIndex = 0;

  const [printing, setPrinting] = useState(false);
  const [formType, setFormType] = useState<FormTypes>(FormTypes.AIFORM);

  const { userName, userEmail, isAdmin } = useAuth();
  const school = (location.state as any)?.school as TAIForm;

  const { errorMessage, render: renderToasts, toastErrorMessage, toastSuccessMessage } = useToasts();
  const [loading, setLoading] = useState(false);

  const schoolForm = useMemo(() => {
    return getUpdatedAIFormValues(Object.assign({}, defaultValues, school) as any);
  }, [school]);

  const methods = useForm({
    mode: 'all',
    defaultValues: schoolForm,
  });

  const processStatus = methods.getValues('processStatus');
  const onSiteStudent = methods.getValues('onsiteStudent');
  const requireTestingPlanUpdates = methods.getValues('requireTestingPlanUpdates');
  const appealEligibleUpdates = methods.getValues('appealEligibleUpdates');

  const needsTestingPlan =
    (onSiteStudent.onsiteTaughtHour && Number(onSiteStudent.onsiteTaughtHour) < 15) ||
    requireTestingPlanUpdates === 'true';
  const showManualError = !!errorMessage;

  const isReadOnly = useMemo(() => {
    return (
      isAdmin ||
      (processStatus === 'rejected' && appealEligibleUpdates !== 'true') ||
      ['processing', 'submit', 'completed', 'appeal', 'international', 'testSecurity', 'opsApproved'].includes(
        processStatus,
      )
    );
  }, [isAdmin, processStatus, appealEligibleUpdates]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [formType]);

  useEffect(() => {
    if (!location.state) {
      navigate(APP_URLS.HOME);
    }
  }, [location.state, navigate]);

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();

    try {
      const isValid = (await methods.trigger()) || Object.keys(methods.formState.errors).length === 0;
      const formData = getAIFormRequestData(methods.getValues(), userName, userEmail);

      if (!isValid) {
        toastErrorMessage('Invalid data - please fill all required fields.');
      } else if (!formData.disclaimer) {
        toastErrorMessage('You must accept the terms before submit the request.');
      } else if (!formData.submitter) {
        toastErrorMessage('You must provide your name to submit the request.');
      } else if (!checkSchoolFormValidation(formData)) {
        toastErrorMessage('Invalid data - please fill the form in correct format.');
      } else if (isValid) {
        //TODO: can we use the same loading state for both saving and submitting instead of having
        // two states like: isSaving state and isSubmitting state?
        setLoading(true);
        try {
        await submitAIForm(formData);
        setLoading(false);
        toastErrorMessage(null);
        navigate(APP_URLS.HOME);     
        } catch (error: any) {
        console.log('Error occurred while submitting school data :' + error.message);
        setLoading(false);
        toastErrorMessage(error.message);
        }
      } else {
        toastErrorMessage('Invalid data - please fill all required fields.');
      }
    } catch (error: any) {
      toastErrorMessage(error.message);
    }
  };

  const handleSaveProgress = async () => {
    const formData = methods.getValues();
    setLoading(true);
    try {
      await saveAIForm(formData);
      setLoading(false);
      toastErrorMessage(null);
      navigate(APP_URLS.SCHOOL, { state: { school: formData } });
    } catch (error: any) {
    console.log('Error occurred while saving school data : ' + error.message);
    setLoading(false);
    toastErrorMessage(error.message);
  }
  };

  const handleClose = () => {
    navigate(APP_URLS.HOME);
  };

  const handleUploadFile = async (file: File, description: string, path?: string) => {
    try {
      const fileS3Key = await uploadSchoolFile(userName, school, file, description, path);
      toastSuccessMessage('File uploaded successfully.');
      return fileS3Key;
    } catch (error: any) {
      toastErrorMessage(error.message);
      return null;
    }
  };

  const handleClickPrintPDF = async () => {
    try {
      setPrinting(true);
      await delay(300);
      await generatePDF(printRefs.current.filter((each) => each) as HTMLDivElement[], school.schoolName);
    } finally {
      setPrinting(false);
    }
  };

  const handleVisitNextScreen = async () => {
    switch (formType) {
      case FormTypes.AIFORM:
        if (needsTestingPlan) {
          setFormType(FormTypes.TESTINGPLAN);
        } else {
          setFormType(FormTypes.SUBMIT);
        }
        break;
      case FormTypes.TESTINGPLAN:
        setFormType(FormTypes.SUBMIT);
        break;
      default:
        break;
    }
  };

  const handleClickNext = async () => {
    if (isReadOnly) {
      handleVisitNextScreen();
      return;
    }

    try {
      const isValid = (await methods.trigger()) || Object.keys(methods.formState.errors).length === 0;
      const formData = getAIFormRequestData(methods.getValues(), userName, userEmail);

      if (!isValid) {
        toastErrorMessage('Invalid data - please fill all required fields.');
      } else if (!checkSchoolFormValidation(formData)) {
        toastErrorMessage('Invalid data - please fill the form in correct format.');
      } else if (!isValid) {
        toastErrorMessage('Invalid data - please fill all required fields.');
      } else {
        setLoading(true);
        try {
        await saveAIForm(formData);
        setLoading(false);
        navigate(APP_URLS.SCHOOL, { state: { school: formData } });
        toastErrorMessage(null);
        handleVisitNextScreen();
        } catch (error: any) {
        console.log('Error occurred while saving school data for Next button: ' + error.message);
        setLoading(false);
        toastErrorMessage(error.message);
        }
      }
    } catch (error: any) {
      toastErrorMessage(error.message);
    }
  };

  return (
    <AIFormProvider data={school}>
      <FormProvider {...methods}>
        {renderToasts()}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ padding: 2 }}>
            <Grid item xs={12} sx={{ borderBottom: `1px solid ${COLORS.borderNavy}`, pb: 2 }}>
              <SchoolHeader printing={printing} onPrintPDF={handleClickPrintPDF} />
            </Grid>

            <div style={{ display: formType === FormTypes.AIFORM ? 'block' : 'none' }}>
              {schoolForms.groups.map((group) => (
                <FormGroup
                  key={group.groupName}
                  group={group}
                  isReadOnly={isReadOnly}
                  ref={(ref) => (printRefs.current[refIndex++] = ref)}
                  showManualError={showManualError}
                  onUploadFile={handleUploadFile}
                />
              ))}
            </div>

            {formType !== FormTypes.AIFORM && needsTestingPlan && (
              <div style={{ display: formType === FormTypes.TESTINGPLAN ? 'block' : 'none' }}>
                {testingPlanForms.groups.map((group) => (
                  <FormGroup
                    key={group.groupName}
                    group={group}
                    isReadOnly={isReadOnly}
                    ref={(ref) => (printRefs.current[refIndex++] = ref)}
                    showManualError={showManualError}
                    onUploadFile={handleUploadFile}
                  />
                ))}
              </div>
            )}

            {formType !== FormTypes.SUBMIT && !isReadOnly && (
              <Grid item xs={12} sx={{ mb: 2, mt: 2 }}>
                {loading && <LinearProgress />}
                <SecondaryButton
                  type="button"
                  style={{ width: '100%' }}
                  disabled={loading}
                  onClick={handleSaveProgress}>
                  Save My Progress
                </SecondaryButton>
              </Grid>
            )}

            {formType !== FormTypes.SUBMIT && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <PrimaryButton type="button" style={{ width: '100%' }} onClick={handleClickNext}>
                  Next
                </PrimaryButton>
              </Grid>
            )}

            {formType === FormTypes.SUBMIT && (
              <Grid item xs={12}>
                <FormGroup
                  group={formSubmitGroup}
                  isReadOnly={isReadOnly}
                  showManualError={showManualError}
                  ref={(ref) => (printRefs.current[refIndex++] = ref)}
                  onUploadFile={handleUploadFile}
                />
              </Grid>
            )}

            {formType === FormTypes.SUBMIT && !isReadOnly && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                {loading && <LinearProgress />}
                <PrimaryButton type="submit" style={{ width: '100%' }} disabled={loading}>
                  Submit My Request
                </PrimaryButton>
              </Grid>
            )}

            {isReadOnly && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <SecondaryButton type="button" style={{ width: '100%' }} disabled={loading} onClick={handleClose}>
                  Close
                </SecondaryButton>
              </Grid>
            )}
          </Grid>
        </form>
      </FormProvider>
    </AIFormProvider>
  );
};

export default School;
